import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search, X, PawPrint, Users, Calendar, Syringe, Stethoscope,
  Home, BarChart3, Building2, FileText, ArrowRight, Sparkles,
  Command,
} from 'lucide-react';
import { getPets } from '../../services/petService';
import { getOwners } from '../../services/ownerService';
import { getAppointments } from '../../services/appointmentService';
import { normalizeTr } from '../../utils/normalizeTr';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

type ResultGroup = {
  label: string;
  icon: any;
  color: string;
  items: ResultItem[];
};

type ResultItem = {
  id: string;
  title: string;
  subtitle?: string;
  icon: any;
  color: string;
  action: () => void;
};

const QUICK_ACTIONS = [
  { label: 'Kontrol Paneli', icon: Home, path: '/app' },
  { label: 'Hayvan Sahipleri', icon: Users, path: '/app/owners' },
  { label: 'Hastalar', icon: PawPrint, path: '/app/pets' },
  { label: 'Randevular', icon: Calendar, path: '/app/appointments' },
  { label: 'Muayeneler', icon: Stethoscope, path: '/app/examinations' },
  { label: 'Aşılar', icon: Syringe, path: '/app/vaccinations' },
  { label: 'İstatistikler', icon: BarChart3, path: '/app/statistics' },
  { label: 'Hasta Raporu', icon: FileText, path: '/app/patient-report' },
  { label: 'Klinik Ayarları', icon: Building2, path: '/app/clinic-settings' },
];

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const { data: pets = [] } = useQuery({ queryKey: ['pets'], queryFn: getPets, enabled: isOpen });
  const { data: owners = [] } = useQuery({ queryKey: ['owners'], queryFn: getOwners, enabled: isOpen });
  const { data: appointments = [] } = useQuery({ queryKey: ['appointments'], queryFn: getAppointments, enabled: isOpen });

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const groups = useMemo<ResultGroup[]>(() => {
    const q = normalizeTr(search);

    // Hızlı navigasyon
    const nav = QUICK_ACTIONS
      .filter(a => !q || normalizeTr(a.label).includes(q))
      .map(a => ({
        id: `nav-${a.path}`,
        title: a.label,
        subtitle: 'Sayfaya git',
        icon: a.icon,
        color: 'text-gray-600 bg-gray-100',
        action: () => { navigate(a.path); onClose(); },
      }));

    if (!q) {
      return [{ label: 'Sayfalar', icon: ArrowRight, color: 'text-gray-500', items: nav }];
    }

    // Hayvan arama
    const petResults = pets
      .filter((p: any) =>
        normalizeTr(p.name).includes(q) ||
        normalizeTr(p.breed).includes(q) ||
        normalizeTr(p.protocolNo).includes(q)
      )
      .slice(0, 5)
      .map((p: any) => ({
        id: `pet-${p.id}`,
        title: p.name,
        subtitle: `${p.breed || '—'} · ${p.species === 'dog' ? 'Köpek' : p.species === 'cat' ? 'Kedi' : 'Hayvan'}`,
        icon: PawPrint,
        color: 'text-amber-600 bg-amber-100',
        action: () => { navigate('/app/pets'); onClose(); },
      }));

    // Sahip arama
    const ownerResults = owners
      .filter((o: any) =>
        normalizeTr(o.name).includes(q) ||
        normalizeTr(o.phone).includes(q) ||
        normalizeTr(o.email).includes(q)
      )
      .slice(0, 5)
      .map((o: any) => ({
        id: `owner-${o.id}`,
        title: o.name,
        subtitle: o.phone || o.email || 'Hayvan sahibi',
        icon: Users,
        color: 'text-teal-600 bg-teal-100',
        action: () => { navigate('/app/owners'); onClose(); },
      }));

    // Randevu arama
    const appointmentResults = appointments
      .filter((a: any) =>
        normalizeTr(a.notes).includes(q) ||
        normalizeTr(a.type).includes(q)
      )
      .slice(0, 3)
      .map((a: any) => ({
        id: `appt-${a.id}`,
        title: `${new Date(a.date).toLocaleDateString('tr-TR')} ${a.time || ''}`,
        subtitle: `${a.type === 'checkup' ? 'Kontrol' : a.type === 'vaccination' ? 'Aşı' : a.type === 'surgery' ? 'Ameliyat' : 'Randevu'}${a.notes ? ` · ${a.notes.slice(0, 30)}` : ''}`,
        icon: Calendar,
        color: 'text-cyan-600 bg-cyan-100',
        action: () => { navigate('/app/appointments'); onClose(); },
      }));

    const result: ResultGroup[] = [];
    if (petResults.length) result.push({ label: 'Hastalar', icon: PawPrint, color: 'text-amber-500', items: petResults });
    if (ownerResults.length) result.push({ label: 'Hayvan Sahipleri', icon: Users, color: 'text-teal-500', items: ownerResults });
    if (appointmentResults.length) result.push({ label: 'Randevular', icon: Calendar, color: 'text-cyan-500', items: appointmentResults });
    if (nav.length) result.push({ label: 'Sayfalar', icon: ArrowRight, color: 'text-gray-500', items: nav });

    return result;
  }, [search, pets, owners, appointments, navigate, onClose]);

  // Tüm sonuçları düzleştir (klavye navigasyonu için)
  const flatResults = useMemo(() => groups.flatMap(g => g.items), [groups]);

  // Klavye navigasyonu
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        flatResults[activeIndex]?.action();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, flatResults, activeIndex, onClose]);

  useEffect(() => { setActiveIndex(0); }, [search]);

  if (!isOpen) return null;

  let runningIndex = -1;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          {/* Arama input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
            <Search className="w-5 h-5 text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hasta, sahip, randevu ara... veya sayfaya git"
              className="flex-1 bg-transparent outline-none text-base text-gray-900 placeholder:text-gray-400"
            />
            <kbd className="px-1.5 py-0.5 text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-200 rounded shrink-0">
              ESC
            </kbd>
          </div>

          {/* Sonuçlar */}
          <div className="max-h-[60vh] overflow-y-auto">
            {flatResults.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Sonuç bulunamadı</p>
                <p className="text-xs text-gray-500">Başka bir terim deneyin</p>
              </div>
            ) : (
              groups.map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.label} className="py-1">
                    <div className="px-4 py-1.5 flex items-center gap-1.5">
                      <GroupIcon className={`w-3 h-3 ${group.color}`} />
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                        {group.label}
                      </p>
                    </div>
                    {group.items.map(item => {
                      runningIndex++;
                      const isActive = runningIndex === activeIndex;
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={item.action}
                          onMouseEnter={() => setActiveIndex(runningIndex)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                            isActive ? 'bg-teal-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center shrink-0`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{item.title}</p>
                            {item.subtitle && (
                              <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
                            )}
                          </div>
                          {isActive && (
                            <ArrowRight className="w-4 h-4 text-teal-500 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer — ipuçları */}
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded font-bold">↑↓</kbd>
                gez
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded font-bold">↵</kbd>
                seç
              </span>
            </div>
            <div className="flex items-center gap-1 font-semibold">
              <Command className="w-3 h-3" />
              <span>+</span>
              <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded font-bold">K</kbd>
              <span>her yerden aç</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CommandPalette;
